"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useRouter } from "@/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import type { Route } from "next";
import { Icon, Numeric } from "@wmart/ui";
import type {
  StorefrontCategoryNode,
  StorefrontProductSummary,
} from "@wmart/shared-types";
import { ApiError } from "../lib/api-client";

/**
 * Header typeahead — debounced live search across products + categories.
 *
 * Behavior:
 *  - 2+ characters → 250 ms debounce → product fetch with limit=8
 *  - Category matches resolved client-side from a once-per-mount fetch of the
 *    full /v1/storefront/categories tree (small, rarely changes, cacheable)
 *  - In-flight product requests are aborted by the next keystroke
 *  - ↑/↓ navigate product-row highlights, Enter on a highlight → that PDP,
 *    Enter with no highlight → /products?q=…
 *  - Category rows are click-only (kept off the arrow-key cycle so the
 *    keyboard model stays simple — products are the dominant intent)
 *  - Esc closes the popover and blurs
 *  - Clicking outside closes; refocusing reopens if there's still a query
 *
 * For anon/pending users the API returns rows without `starting_unit_price`,
 * so each product row simply renders title + thumbnail. Approved users get £.
 *
 * Visual contract: docs/design/templates/Wmart_v1/screens-storefront.jsx —
 * header search input (line 58-65). The popover is not in the mockup; it's the
 * functional upgrade requested over the static placeholder.
 */

interface SearchResponse {
  items: StorefrontProductSummary[];
  nextCursor: string | null;
}

const DEBOUNCE_MS = 250;
const MAX_RESULTS = 8;
const MIN_QUERY = 2;

function formatGBP(minor: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(minor / 100);
}

export function HeaderSearch() {
  const router = useRouter();
  const t = useTranslations("Catalog");
  // Active storefront locale. Client fetches MUST pass this — a browser fetch
  // sends the user's Accept-Language (≈ en), not the page locale, so without it
  // the API searches the English index and an Arabic/Urdu query folds to script
  // absent from the en doc → empty dropdown (the SSR /[locale]/products page
  // passes ?locale=, which is why pressing Enter worked but the typeahead did not).
  const locale = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const [value, setValue] = useState("");
  const [items, setItems] = useState<StorefrontProductSummary[]>([]);
  const [flatCategories, setFlatCategories] = useState<StorefrontCategoryNode[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<number>(-1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  const trimmed = value.trim();
  const hasQuery = trimmed.length >= MIN_QUERY;
  const lcTrimmed = trimmed.toLowerCase();

  const matchedCategories = hasQuery
    ? flatCategories
        .filter((c) => c.title.toLowerCase().includes(lcTrimmed))
        .slice(0, 3)
    : [];

  const closeAndBlur = useCallback(() => {
    setOpen(false);
    setMobileOpen(false);
    setHighlight(-1);
  }, []);

  const submitFullSearch = useCallback(
    (q: string) => {
      const url = `/products?q=${encodeURIComponent(q)}` as Route;
      closeAndBlur();
      router.push(url);
    },
    [router, closeAndBlur],
  );

  const navigateToHit = useCallback(
    (hit: StorefrontProductSummary) => {
      closeAndBlur();
      router.push(`/products/${hit.slug}` as Route);
    },
    [router, closeAndBlur],
  );

  const navigateToCategory = useCallback(
    (cat: StorefrontCategoryNode) => {
      closeAndBlur();
      router.push(`/products?category_slug=${cat.slug}` as Route);
    },
    [router, closeAndBlur],
  );

  // Fetch categories tree once on mount. Failure is silent — categories search
  // is a nice-to-have, products search still works without it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/v1/storefront/categories?locale=${encodeURIComponent(locale)}`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const tree = (await res.json()) as StorefrontCategoryNode[];
        if (cancelled) return;
        const flat: StorefrontCategoryNode[] = [];
        const walk = (nodes: StorefrontCategoryNode[]) => {
          for (const n of nodes) {
            flat.push(n);
            if (n.children.length > 0) walk(n.children);
          }
        };
        walk(tree);
        setFlatCategories(flat);
      } catch {
        // Network failure — leave list empty; product search still functions.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        closeAndBlur();
      }
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [closeAndBlur]);

  useEffect(() => {
    if (!hasQuery) {
      setItems([]);
      setLoading(false);
      setError(null);
      setHighlight(-1);
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/v1/storefront/products?q=${encodeURIComponent(trimmed)}&limit=${MAX_RESULTS}&locale=${encodeURIComponent(locale)}`,
          { signal: ctl.signal, credentials: "include" },
        );
        if (!res.ok) {
          throw await ApiError.fromResponse(res);
        }
        const json = (await res.json()) as SearchResponse;
        setItems(json.items);
        // Do NOT auto-highlight the first row: Enter should open the FULL
        // results page (/products?q=…), not jump to one product. Arrowing down
        // sets a highlight, and then Enter goes to that specific product.
        setHighlight(-1);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof ApiError && err.status === 401) {
          // Search is open to anon — 401 here means the api session reaper
          // expired mid-typing; not a real error.
          setItems([]);
        } else {
          setError("Search is unavailable. Press Enter to open the full catalog.");
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [trimmed, hasQuery, locale]);

  function onInputKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      closeAndBlur();
      inputRef.current?.blur();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && items[highlight]) {
        navigateToHit(items[highlight]);
      } else if (trimmed.length > 0) {
        submitFullSearch(trimmed);
      }
      return;
    }
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? items.length - 1 : h - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlight(items.length - 1);
    }
  }

  const showPopover =
    open &&
    hasQuery &&
    Boolean(
      loading || error || items.length > 0 || matchedCategories.length > 0,
    );
  const activeOptionId =
    highlight >= 0 && items[highlight]
      ? `${listboxId}-opt-${items[highlight].id}`
      : undefined;
  const mobileActiveOptionId =
    highlight >= 0 && items[highlight]
      ? `${listboxId}-m-opt-${items[highlight].id}`
      : undefined;

  const renderResults = (idPrefix: string) => (
    <>
      {loading && items.length === 0 ? (
        <p className="px-4 py-3 text-body-sm text-ink-500">Searching…</p>
      ) : null}

      {error ? (
        <p className="px-4 py-3 text-body-sm text-danger">{error}</p>
      ) : null}

      {!loading &&
      !error &&
      items.length === 0 &&
      matchedCategories.length === 0 &&
      hasQuery ? (
        <p className="px-4 py-3 text-body-sm text-ink-500">
          No matches for <span className="text-ink-900">“{trimmed}”</span>.
          Press Enter to open the full catalog.
        </p>
      ) : null}

      {matchedCategories.length > 0 ? (
        <div role="group" aria-label="Categories">
          <p className="px-4 pb-3 pt-3 text-overline font-semibold uppercase tracking-[0.06em] text-ink-500">
            Categories
          </p>
          {matchedCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => navigateToCategory(cat)}
              className={[
                "flex w-full items-center gap-3 px-3 py-2 text-start",
                "border-b border-ink-100 last:border-b-0",
                "outline-none transition-colors duration-fast hover:bg-paper-warm",
              ].join(" ")}
            >
              <span
                aria-hidden
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xs bg-accent-soft text-accent-deep"
              >
                <Icon name="filter" size={16} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-body-sm font-medium text-ink-900">
                  All in {cat.title}
                </span>
                <Numeric className="text-body-sm text-ink-500">
                  {cat.product_count}{" "}
                  {cat.product_count === 1 ? "product" : "products"}
                </Numeric>
              </span>
              <Icon name="chevronRight" size={14} aria-hidden />
            </button>
          ))}
        </div>
      ) : null}

      {matchedCategories.length > 0 && items.length > 0 ? (
        <p className="mt-2 border-t border-ink-100 px-4 pb-3 pt-4 text-overline font-semibold uppercase tracking-[0.06em] text-ink-500">
          Products
        </p>
      ) : null}

      {items.map((hit, i) => {
        const isActive = i === highlight;
        const price = hit.starting_unit_price;
        return (
          <button
            key={hit.id}
            type="button"
            role="option"
            id={`${idPrefix}-opt-${hit.id}`}
            aria-selected={isActive}
            onMouseEnter={() => setHighlight(i)}
            onClick={() => navigateToHit(hit)}
            className={[
              "flex w-full items-center gap-3 px-3 py-2 text-start",
              "border-b border-ink-100 last:border-b-0",
              "outline-none transition-colors duration-fast",
              isActive ? "bg-accent-soft" : "hover:bg-paper-warm",
            ].join(" ")}
          >
            <img
              src={hit.renditions?.card ?? ""}
              alt=""
              loading="lazy"
              className="h-10 w-10 flex-shrink-0 rounded-xs border border-ink-100 object-cover"
              onError={(e) => {
                // Hide broken images cleanly; the layout doesn't depend on width.
                e.currentTarget.style.visibility = "hidden";
              }}
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-body-sm font-medium text-ink-900">
                {hit.title}
              </span>
              {price ? (
                <Numeric serif className="text-body-sm text-ink-700">
                  from {formatGBP(price.unit_price_minor)}
                </Numeric>
              ) : (
                <span className="text-body-sm text-ink-500">
                  View product
                </span>
              )}
            </span>
            <Icon name="chevronRight" size={14} aria-hidden />
          </button>
        );
      })}

      {items.length > 0 ? (
        <button
          type="button"
          onClick={() => submitFullSearch(trimmed)}
          className={[
            "flex w-full items-center justify-between gap-2 border-t border-ink-200",
            "bg-paper-warm px-3 py-2 text-body-sm font-medium text-accent-deep",
            "outline-none transition-colors duration-fast hover:bg-accent-soft",
          ].join(" ")}
        >
          <span>
            See all results for{" "}
            <span className="text-ink-900">“{trimmed}”</span>
          </span>
          <Icon name="arrowRight" size={14} aria-hidden />
        </button>
      ) : null}
    </>
  );

  return (
    <>
      {/* Desktop inline search (unchanged behaviour) */}
      <div
        ref={rootRef}
        data-slot="header-search"
        /* w-[280px] keeps the same width as before on roomy screens (no visual
           churn); min-w-0 + shrink let it give way under space pressure (long
           company name + cart total) instead of overflowing the header row. */
        className="relative hidden w-[280px] min-w-0 shrink lg:block"
      >
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed.length > 0) submitFullSearch(trimmed);
          }}
        >
          <div
            className={[
              "flex items-center gap-2 rounded-sm border bg-white px-3 py-2",
              "border-ink-200 focus-within:border-accent focus-within:shadow-focus",
            ].join(" ")}
          >
            <span aria-hidden className="text-ink-400">
              <Icon name="search" size={16} />
            </span>
            <input
              ref={inputRef}
              type="search"
              role="combobox"
              aria-expanded={showPopover}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={activeOptionId}
              aria-label="Search the catalog"
              placeholder={t("searchPlaceholder")}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setOpen(true);
              }}
              onFocus={() => {
                if (hasQuery) setOpen(true);
              }}
              onKeyDown={onInputKey}
              className="flex-1 bg-transparent text-body-sm text-ink-900 outline-none placeholder:text-ink-400"
            />
          </div>
        </form>

        {showPopover ? (
          <div
            id={listboxId}
            role="listbox"
            aria-label="Product suggestions"
            className={[
              "absolute inset-x-0 top-full z-30 mt-3",
              "rounded-sm border border-ink-200 bg-white shadow-2",
              "overflow-hidden py-3",
            ].join(" ")}
          >
            {renderResults(listboxId)}
          </div>
        ) : null}
      </div>

      {/* Mobile: search icon trigger (shows in the header right cluster) */}
      <button
        type="button"
        aria-label="Search the catalog"
        aria-expanded={mobileOpen}
        onClick={() => {
          setMobileOpen(true);
          setOpen(true);
        }}
        className="inline-flex items-center justify-center rounded-sm p-2 text-ink-700 outline-none transition-colors duration-fast hover:bg-accent-soft hover:text-ink-900 focus-visible:shadow-focus lg:hidden"
      >
        <Icon name="search" size={20} aria-hidden />
      </button>

      {/* Mobile: full-screen search overlay */}
      {mobileOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Search the catalog"
          className="fixed inset-0 z-50 flex flex-col bg-paper lg:hidden"
        >
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              if (trimmed.length > 0) submitFullSearch(trimmed);
            }}
            className="flex items-center gap-2 border-b border-ink-200 px-4 py-3"
          >
            <span aria-hidden className="text-ink-400">
              <Icon name="search" size={18} />
            </span>
            <input
              ref={mobileInputRef}
              type="search"
              role="combobox"
              aria-expanded={hasQuery}
              aria-controls={`${listboxId}-m`}
              aria-autocomplete="list"
              aria-activedescendant={mobileActiveOptionId}
              aria-label="Search the catalog"
              autoFocus
              placeholder={t("searchPlaceholder")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onInputKey}
              className="min-w-0 flex-1 bg-transparent text-body text-ink-900 outline-none placeholder:text-ink-400"
            />
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                closeAndBlur();
              }}
              className="shrink-0 rounded-sm px-1 py-1 text-body-sm font-medium text-accent outline-none transition-colors duration-fast hover:text-accent-deep focus-visible:shadow-focus"
            >
              Cancel
            </button>
          </form>
          <div
            id={`${listboxId}-m`}
            role="listbox"
            aria-label="Product suggestions"
            className="flex-1 overflow-y-auto py-2"
          >
            {hasQuery ? (
              renderResults(`${listboxId}-m`)
            ) : (
              <p className="px-4 py-6 text-center text-body-sm text-ink-500">
                Type to search products and categories.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
